import React, { useState, useEffect } from "react";
import { Keyring } from '@polkadot/api';
import { mnemonicValidate } from '@polkadot/util-crypto';
import './MnemonicsConfirmationModal.css';

const MnemonicsConfirmationModal = ({
    visible,
    onClose,
    onConfirm,
    currentUser,
    cypherSecret,
}) => {
    const [mnemonics, setMnemonics] = useState("");
    const [publicAddress, setPublicAddress] = useState("");
    const [saveMyMnemonics, setSaveMyMnemonics] = useState(false);
    const [mnemonicsPwd, setMnemonicsPwd] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    // handle modal show/hide with bootstrap
    useEffect(() => {
        const modalElement = document.getElementById("confirmationModal");
        if (!modalElement) return;

        const modal = new window.bootstrap.Modal(modalElement);
        if (visible) modal.show();
        else modal.hide();

        return () => modal.hide();
    }, [visible]);

    const resetModalState = () => {
        setMnemonics("");
        setPublicAddress("");
        setSaveMyMnemonics(false)
        setMnemonicsPwd("")
        setPasswordError("")
        // setSaveMyMnemonics(false);
        // setMnemonicsPwd("");
        // setPasswordError("");
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setMnemonicsPwd(value);
        if (value.length < 6) {
            setPasswordError("Password is too weak");
        } else {
            setPasswordError("");
        }
    };

    const isValid =
        mnemonicValidate(mnemonics) &&
        publicAddress?.toLowerCase() === currentUser?.public_address?.toLowerCase()
    //&&
    const isPasswordValid = saveMyMnemonics === true && mnemonicsPwd.length >= 6

    return (
        <div
            className="modal fade"
            id="confirmationModal"
            tabIndex="-1"
            aria-labelledby="confirmationModalLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content mnemonics-modal-content">
                    {/* Header with gradient accent */}
                    <div className="modal-header border-0 pb-0">
                        <div className="w-100">
                            <div className="d-flex align-items-center mb-2">
                                <div className="mnemonics-icon-wrapper me-3">
                                    <i className="fas fa-shield-alt"></i>
                                </div>
                                <div className="flex-grow-1">
                                    <h5 className="modal-title mb-1 fw-bold" id="confirmationModalLabel">
                                        Secure Transaction Verification
                                    </h5>
                                    <p className="text-muted small mb-0">
                                        Verify your identity to complete this transaction
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={() => {
                                resetModalState();
                                onClose?.();
                            }}
                        ></button>
                    </div>

                    <div className="modal-body pt-3">
                        {/* Security notice */}
                        <div className="security-notice mb-4">
                            <div className="d-flex align-items-start">
                                <i className="fas fa-lock me-2 mt-1"></i>
                                <div className="flex-grow-1">
                                    <small className="text-muted">
                                        <strong>Your keys stay private.</strong> We never store your mnemonic phrase.
                                        It's only used locally to sign this transaction.
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Mnemonic Input */}
                        <div className="mb-4">
                            <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                <i className="fas fa-key me-2 text-muted"></i>
                                Recovery Phrase (Mnemonic)
                            </label>
                            <div className="position-relative">
                                <textarea
                                    className={`form-control shadow-none mnemonics-textarea ${!mnemonics ? "" : isValid ? "is-valid-custom" : "is-invalid-custom"
                                        }`}
                                    onChange={(e) => {
                                        if (mnemonicValidate(e.target.value)) {
                                            const keyring = new Keyring({
                                                type: 'sr25519',
                                                ss58Format: 42,
                                            });
                                            setMnemonics(e.target.value)
                                            const newPair = keyring.addFromUri(e.target.value);
                                            setPublicAddress(newPair.address)
                                        } else {
                                            setMnemonics(e.target.value)
                                            setPublicAddress(null)
                                        }
                                    }}
                                    value={mnemonics}
                                    name="mnemonics"
                                    rows="4"
                                    placeholder="Enter your 12 or 24-word recovery phrase..."
                                ></textarea>
                                {mnemonics && (
                                    <div className="validation-icon">
                                        {isValid ? (
                                            <i className="fas fa-check-circle text-success"></i>
                                        ) : (
                                            <i className="fas fa-times-circle text-danger"></i>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Address match status */}
                        {publicAddress && (
                            <div className={`address-status-card mb-4 ${publicAddress.toLowerCase() === currentUser?.public_address?.toLowerCase()
                                ? 'success-card'
                                : 'error-card'
                                }`}>
                                {publicAddress.toLowerCase() === currentUser?.public_address?.toLowerCase() ? (
                                    <div className="d-flex align-items-start">
                                        <div className="status-icon-wrapper success me-3">
                                            <i className="fas fa-check"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <strong className="text-success me-2">Verified Successfully!</strong>
                                                <span className="badge bg-success-subtle text-success">
                                                    <i className="fas fa-shield-check me-1"></i>
                                                    Address Match
                                                </span>
                                            </div>
                                            <p className="mb-2 small">Your wallet address has been verified</p>
                                            <div className="address-display">
                                                <code className="small">{publicAddress}</code>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-start">
                                        <div className="status-icon-wrapper error me-3">
                                            <i className="fas fa-exclamation-triangle"></i>
                                        </div>
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <strong className="text-danger me-2">Address Mismatch</strong>
                                                <span className="badge bg-danger-subtle text-danger">
                                                    <i className="fas fa-times me-1"></i>
                                                    Verification Failed
                                                </span>
                                            </div>
                                            <p className="mb-2 small">
                                                The recovery phrase doesn't match your account. Please use the phrase
                                                you created during sign-up.
                                            </p>
                                            <div className="address-display">
                                                <code className="small">{publicAddress}</code>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Save mnemonics option */}
                        {publicAddress && publicAddress.toLowerCase() === currentUser?.public_address?.toLowerCase() && (
                            <div className="save-mnemonics-section">
                                <div className="form-check custom-checkbox">
                                    <input
                                        checked={saveMyMnemonics}
                                        onChange={() => setSaveMyMnemonics(!saveMyMnemonics)}
                                        className="form-check-input"
                                        type="checkbox"
                                        id="mnemonicsForSingleNFT"
                                    />
                                    <label className="form-check-label" htmlFor="mnemonicsForSingleNFT">
                                        <div className="d-flex align-items-start">
                                            <div>
                                                <strong className="d-block">Save for future transactions</strong>
                                                <small className="text-muted">
                                                    Your phrase will be encrypted and stored locally in your browser
                                                </small>
                                            </div>
                                        </div>
                                    </label>
                                </div>

                                {saveMyMnemonics && (
                                    <div className="password-section mt-3">
                                        <label className="form-label fw-semibold d-flex align-items-center mb-2">
                                            <i className="fas fa-fingerprint me-2 text-muted"></i>
                                            Encryption Password
                                        </label>
                                        <div className="position-relative">
                                            <input
                                                value={mnemonicsPwd}
                                                onChange={handlePasswordChange}
                                                className={`form-control shadow-none ${passwordError ? 'is-invalid-custom' :
                                                    mnemonicsPwd.length >= 6 ? 'is-valid-custom' : ''
                                                    }`}
                                                type={showPassword ? "text" : "password"}
                                                name="password"
                                                placeholder="Create a strong password (min. 6 characters)"
                                            />
                                            <button
                                                type="button"
                                                className="password-toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                            </button>
                                        </div>
                                        {passwordError && (
                                            <div className="password-feedback mt-2">
                                                <small className="text-danger">
                                                    <i className="fas fa-exclamation-circle me-1"></i>
                                                    {passwordError}
                                                </small>
                                            </div>
                                        )}
                                        {!passwordError && mnemonicsPwd.length >= 6 && (
                                            <div className="password-feedback mt-2">
                                                <small className="text-success">
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    Password strength: Good
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer border-0 pt-0">
                        <button
                            type="button"
                            className="btn btn-outline-dark px-4 py-2"
                            data-bs-dismiss="modal"
                            onClick={() => {
                                resetModalState();
                                onClose?.();
                            }}
                        >
                            <i className="fas fa-times me-2"></i>
                            Cancel
                        </button>
                        <button
                            disabled={!isValid || (saveMyMnemonics && mnemonicsPwd.length < 6)}
                            onClick={() => {

                                onConfirm?.({ mnemonics, publicAddress, saveMyMnemonics, mnemonicsPwd });
                                resetModalState();
                            }}
                            type="button"
                            className="btn BtnBlack px-4 py-2"
                        >
                            <i className="fas fa-check-circle me-2"></i>
                            Confirm Transaction
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MnemonicsConfirmationModal;
